/**
 * Explicit-sign-out gate for the FullSwing partner auth flow (FSG-8815).
 *
 * Why this exists: on web, react-native-web captures `window.location.href`
 * ONCE at module load and `Linking.getInitialURL()` returns that cached value
 * for the life of the page. The QR deep link (`/partner-auth?id_token=...&
 * session_id=...`) is therefore recoverable by EVERY PartnerAuth mount in the
 * same page load — including the post-logout `navigation.reset(StartingPage)`,
 * which used to re-run the token login and re-join the player to the game
 * session milliseconds after they signed out ("logout leaves player in game",
 * the FSG-8815 reopen).
 *
 * The latch is deliberately IN-MEMORY, never persisted: a genuine new QR scan
 * is a fresh document load with fresh module state, so it can never be
 * swallowed by a stale latch. The two re-auth vectors inside one page load are
 * each covered:
 *
 *   1. Same-instance PartnerAuth remount (navigation.reset after logout) —
 *      blocked by the latch, consumed one-shot by PartnerAuth's auth effect.
 *   2. The sign-out handlers' `window.location.reload()` ~150ms later —
 *      blocked by scrubbing id_token/session_id out of the address bar here,
 *      so the reloaded page (whose fresh "initial URL" is whatever the bar
 *      holds at reload time) has no credentials to recover.
 */

let explicitSignOut = false;

/**
 * Arm the gate. Call from `logout()` (the common path for menu sign-out,
 * account-nav sign-out, and idle-expiry walk-away) BEFORE any teardown, so a
 * failure later in logout cannot leave the gate unarmed. Also scrubs the
 * deep-link credentials from the browser URL (web only; no-op elsewhere).
 */
export function markExplicitPartnerSignOut(): void {
  explicitSignOut = true;
  scrubDeepLinkCredentialsFromUrl();
}

/**
 * One-shot read: reports whether an explicit sign-out armed the gate, and
 * disarms it. PartnerAuth calls this at the top of its auto-auth effect; a
 * `true` means "do not recover deep-link credentials for this mount".
 */
export function consumeExplicitPartnerSignOut(): boolean {
  const v = explicitSignOut;
  explicitSignOut = false;
  return v;
}

function scrubDeepLinkCredentialsFromUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const param of ['id_token', 'session_id']) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch (e) {
    // Malformed href or a non-browser environment pretending to be one —
    // nothing scrubbable; the in-memory latch still covers the remount vector.
    console.log('partnerSignOutGate: URL scrub skipped', e);
  }
}
